import type { PropsWithChildren } from 'react';
import { memo, useMemo, useRef } from 'react';
import type { ReactNodeLike } from 'prop-types';
import { OptimizelyProvider } from '@optimizely/react-sdk';

import OptimizelyNotifications from 'Components/OptimizelyNotifications/OptimizelyNotifications';
import type { ConfigDatafile } from 'Services/optimizely/optimizely';
import {
	getUserAttributesPayload,
	isUsableOptimizelyDatafile,
	optimizelyInstanceFactory,
} from 'Services/optimizely/optimizely';
import type { User } from 'Types/user';

interface OptimizelyInitialiserProps {
	children: NonNullable<ReactNodeLike>;
	optimizelyDatafile: ConfigDatafile;
	user?: User;
}

export const OptimizelyInitialiserComponent: React.FC<PropsWithChildren<OptimizelyInitialiserProps>> = ({
	children,
	optimizelyDatafile,
	user,
}) => {
	// Only initialise the SDK when the datafile is genuinely usable. A malformed
	// datafile (e.g. an S3 `AccessDenied` XML body returned locally) would create
	// a client that never becomes ready and spams "client not ready" warnings on
	// every render, flooding the logs and slowing things down.
	const datafileRef = useRef(isUsableOptimizelyDatafile(optimizelyDatafile) ? optimizelyDatafile : null);
	const optimizelyInstanceRef = useRef(datafileRef.current ? optimizelyInstanceFactory(datafileRef.current) : null);
	const optimizelyInstance = optimizelyInstanceRef.current;

	const optimizelyUser = useMemo(
		() => ({
			attributes: {
				...getUserAttributesPayload({
					dealerContactId: user?.id,
					dealershipId: user?.dealerId,
				}),
			},
			id: user ? String(user.id) : null,
		}),
		[user?.id, user?.dealerId],
	);

	if (!user || !datafileRef.current || !optimizelyInstance) {
		return <>{children}</>;
	}

	return (
		<OptimizelyProvider optimizely={optimizelyInstance} user={optimizelyUser}>
			<OptimizelyNotifications />
			{children}
		</OptimizelyProvider>
	);
};

export const OptimizelyInitialiser = memo(OptimizelyInitialiserComponent);
